import { isBefore } from "date-fns";
import { ProspectSchoolDTO } from "../types/Prospect";
import { SchoolAttendingStatus, SchoolTypeName } from "../types/School";

export const isFuture = (school: ProspectSchoolDTO): boolean =>
  school.schoolAttendingStatusId === SchoolAttendingStatus.SIGNED_NLI ||
  school.schoolAttendingStatusId === SchoolAttendingStatus.VERBAL_COMMIT;

export const isCurrent = (school: ProspectSchoolDTO): boolean =>
  school.schoolAttendingStatusId === SchoolAttendingStatus.ATTENDING;

export const isPrevious = (school: ProspectSchoolDTO): boolean =>
  school.schoolAttendingStatusId === SchoolAttendingStatus.ATTENDED ||
  school.schoolAttendingStatusId === SchoolAttendingStatus.GRADUATED;

export const isCollege = (school: ProspectSchoolDTO): boolean =>
  !!school.schoolTypeName &&
  (school.schoolTypeName === SchoolTypeName.FOUR_YEAR_COLLEGE ||
    school.schoolTypeName === SchoolTypeName.JUNIOR_COLLEGE);

export const isHighSchool = (school: ProspectSchoolDTO): boolean =>
  !!school.schoolTypeName && school.schoolTypeName === SchoolTypeName.HIGH_SCHOOL;

export const validStartDate = (school: ProspectSchoolDTO): boolean => {
  const startDate = !!school.startDate ? new Date(school.startDate) : null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const future = isFuture(school);
  // future school start date must be in future else start date must be in past
  return (future && !!startDate && startDate >= now) || (!future && !!startDate && startDate <= now);
};

export const validEndDate = (school: ProspectSchoolDTO): boolean => {
  const endDate = !!school.endDate ? new Date(school.endDate) : null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const previous = isPrevious(school);
  // previous school end date must be in past else end date must be in future
  return (previous && !!endDate && endDate <= now) || (!previous && !!endDate && endDate >= now);
};

export const validSchool = (school: ProspectSchoolDTO): boolean => {
  const isNoSchool = school.ebisSchoolId == 0;
  const startDate = !!school.startDate ? new Date(school.startDate) : null;
  const endDate = !!school.endDate ? new Date(school.endDate) : null;

  const mandatory =
    !!(
      school.schoolTypeId &&
      school.schoolName &&
      school.schoolState &&
      school.ebisSchoolId &&
      !!startDate &&
      !isNaN(startDate.getTime()) &&
      !!endDate &&
      !isNaN(endDate.getTime()) &&
      isBefore(startDate, endDate)
    ) || isNoSchool;

  const college: boolean = isCollege(school);
  const current: boolean = isCurrent(school);
  const academicClass: boolean = !current || !!school.academicClassId || isNoSchool;
  const classDifferent: boolean = !(college && current) || school.schoolClassDifferent != null;
  const shirted: boolean = isFuture(school) || !college || school.hasShirted != null;
  const ninth: boolean = !isHighSchool(school) || !current || school.ninthGradeYear != null;

  const isValidStartDate: boolean = validStartDate(school);
  const isValidEndDate: boolean = validEndDate(school);

  return mandatory && academicClass && classDifferent && shirted && ninth && isValidStartDate && isValidEndDate;
};

export const wentToCanadianHS = (current: ProspectSchoolDTO | undefined, previous: ProspectSchoolDTO[]): boolean =>
  (current?.schoolCountry === "Canada" && isHighSchool(current)) ||
  previous.some((school) => school.schoolCountry === "Canada" && isHighSchool(school));
